import { DatePipe } from '@angular/common';
import {
  compareEnergyDevicePowerPeriods,
  EnergyDevice,
  getEnergyDevicePowerPeriodLabel,
  getEnergyDeviceTypeLabel,
  isEnergyDeviceType,
} from '../../../../energia/models/energy-device.model';
import { EntityPopup, PopupInfoItem } from './entity-popup.interface';

function getNumericMeasurementValue(
  measurement: EnergyDevice['latest_measurement'],
  key: string
): number | null {
  const value = measurement?.[key];

  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getContractedPowerLabel(device: EnergyDevice): string | null {
  const tariffRate = device.tariff?.rate;
  const periods = device.tariff?.power_by_periods ?? [];

  const periodLabels = [...periods]
    .filter(
      entry =>
        typeof entry?.period === 'string' &&
        entry.period.trim() !== '' &&
        typeof entry?.power === 'number' &&
        Number.isFinite(entry.power)
    )
    .sort((left, right) =>
      compareEnergyDevicePowerPeriods(left.period, right.period)
    )
    .map(entry => {
      const periodLabel = getEnergyDevicePowerPeriodLabel(
        entry.period,
        tariffRate
      );

      return `${periodLabel}: ${entry.power}kW`;
    });

  if (periodLabels.length === 0) {
    return null;
  }

  return `Potencia contratada ${periodLabels.join(', ')}`;
}

export class DispositivoEnergiaPopup implements EntityPopup {
  private readonly datePipe = new DatePipe('es-ES');

  constructor(private readonly device: EnergyDevice) {}

  getTitle(): string {
    const typeLabel = isEnergyDeviceType(this.device.device_type)
      ? getEnergyDeviceTypeLabel(this.device.device_type)
      : 'Dispositivo de energía';

    return `${typeLabel} ${this.device.reference_name}`.trim();
  }

  getBadgeLines(): string[] {
    return [
      `CUPS: ${this.device.cups || '-'}`,
      `Tarifa: ${this.device.tariff?.rate || '-'}`,
      `Proveedor: ${this.device.provider || '-'} / Id del Proveedor: ${
        this.device.provider_device_id || '-'
      }`,
    ];
  }

  getBadgeText(): string {
    return this.getBadgeLines().join('\n');
  }

  getInfoItems(): PopupInfoItem[] {
    const measurement = this.device.latest_measurement;
    const measurementDate = this.formatMeasurementDate(
      measurement?.timestamp ?? this.device.latest_measurement_at
    );
    const contractedPowerLabel = getContractedPowerLabel(this.device);

    return [
      {
        icon: 'bolt',
        label: 'Último consumo de energía activa',
        value: getNumericMeasurementValue(
          measurement,
          'delta_active_energy_total'
        ),
        unit: 'kWh',
        date: measurementDate,
      },
      {
        icon: 'bolt',
        label: 'Último consumo de energía reactiva',
        value: getNumericMeasurementValue(
          measurement,
          'delta_reactive_energy_total'
        ),
        unit: 'kVArh',
        date: measurementDate,
      },
      {
        icon: 'readiness_score',
        label: 'Potencia',
        value: getNumericMeasurementValue(measurement, 'active_power_total'),
        unit: 'kW',
        date: measurementDate,
        secondaryText: contractedPowerLabel ?? undefined,
      },
      {
        icon: 'readiness_score',
        label: 'Factor de potencia',
        value:
          getNumericMeasurementValue(measurement, 'power_factor_total') ??
          getNumericMeasurementValue(measurement, 'mean_power_factor_total'),
        maximumFractionDigits: 3,
      },
    ];
  }

  private formatMeasurementDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return (
      this.datePipe.transform(value, 'dd/MM/yyyy HH:mm:ss', 'UTC') ??
      this.datePipe.transform(value, 'dd/MM/yyyy HH:mm:ss') ??
      ''
    );
  }
}
